import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import { ClassNames } from '@emotion/core';
import cx from 'classnames';
import { CardElement, injectStripe } from 'react-stripe-elements';

import config from 'config';
import Button from 'common/button';

const baseStripeStyle = {
    fontFamily: config('/tailwind/fonts/sans').join(', '),
    fontSize: '16px',
    '::placeholder': {
        color: config('/tailwind/colors/black30'),
    },
};

class CardFields extends Component {
    static propTypes = {
        buttonLabel: PropTypes.string.isRequired,
        onSubmit: PropTypes.func.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
        showFlash: PropTypes.func.isRequired,
        stripe: PropTypes.shape({
            createToken: PropTypes.func,
        }),
    };

    static defaultProps = {
        stripe: null,
    };

    handleSubmit = async (values, { setSubmitting }) => {
        const { stripe, showFlash, onSubmit } = this.props;

        try {
            setSubmitting(true);

            const fullName = [values.firstName, values.lastName].join(' ');
            const { token, error } = await stripe.createToken({ name: fullName });
            if (error) throw error;

            onSubmit({
                ...values,
                token: token.id,
                card: {
                    brand: token.card.brand,
                    last4: token.card.last4,
                },
            });
        } catch (err) {
            setSubmitting(false);

            showFlash({
                type: 'danger',
                title: 'Card error',
                message: err.message,
                duration: 5000,
            });
        }
    };

    render() {
        const { firstName, lastName, email, buttonLabel } = this.props;

        return (
            <Formik
                initialValues={{ firstName, lastName, email }}
                validationSchema={yup.object().shape({
                    firstName: yup.string().required('First name is required'),
                    lastName: yup.string().required('Last name is required'),
                    email: yup
                        .string()
                        .email('Invalid email address')
                        .required('Email is required'),
                })}
                onSubmit={this.handleSubmit}
            >
                {({ errors, touched, isSubmitting }) => (
                    <Form className="mt-4">
                        <h3 className="text-lg text-center font-bold leading-normal text-gray-70">
                            Enter Card Info
                        </h3>

                        <div className="mt-8">
                            <div className="flex flex-wrap -ml-2 -mt-2">
                                <div className="w-full pl-2 pt-2">
                                    <Field
                                        type="text"
                                        name="firstName"
                                        placeholder="First Name"
                                        className={cx('input-default w-full', {
                                            'is-invalid': errors.firstName && touched.firstName,
                                        })}
                                    />
                                    <ErrorMessage
                                        component="p"
                                        className="text-red mt-2 text-12 text-center"
                                        name="firstName"
                                    />
                                </div>
                                <div className="w-full pl-2 pt-2">
                                    <Field
                                        type="text"
                                        name="lastName"
                                        placeholder="Last Name"
                                        className={cx('input-default w-full', {
                                            'is-invalid': errors.lastName && touched.lastName,
                                        })}
                                    />
                                    <ErrorMessage
                                        component="p"
                                        className="text-red mt-2 text-12 text-center"
                                        name="lastName"
                                    />
                                </div>
                                <div className="w-full pl-2 pt-2">
                                    <Field
                                        type="email"
                                        name="email"
                                        placeholder="Email"
                                        className={cx('input-default w-full', {
                                            'is-invalid': errors.email && touched.email,
                                        })}
                                    />
                                    <ErrorMessage
                                        component="p"
                                        className="text-red mt-2 text-12 text-center"
                                        name="email"
                                    />
                                </div>

                                <ClassNames>
                                    {({ css }) => {
                                        // Stripe Elements don't accept a CSS prop for use with emotion,
                                        // so we generate classnames to pass in instead.
                                        const base = css`
                                            font-family: ${config('/tailwind/fonts/sans').join(
                                                ', '
                                            )};
                                            border-radius: ${config('/tailwind/borderRadius/sm')};
                                            padding: ${config('/tailwind/padding/6')}
                                                ${config('/tailwind/padding/8')};
                                            width: 100%;
                                            max-width: 100%;
                                            background: ${config('/tailwind/colors/gray10')};
                                            border: 1px solid ${config('/tailwind/colors/gray10')};
                                        `;

                                        const focus = css`
                                            background: ${config('/tailwind/colors/blue10')};
                                            border-color: ${config('/tailwind/colors/blue30')};
                                            box-shadow: 0 0 8px ${config('/tailwind/colors/blue10')};
                                            outline: none;
                                        `;

                                        const invalid = css`
                                            background: ${config('/tailwind/colors/red10')};
                                            border-color: ${config('/tailwind/colors/red30')};
                                            box-shadow: 0 0 8px ${config('/tailwind/colors/red10')};
                                            outline: none;
                                        `;

                                        return (
                                            <>
                                                <div className="w-full pl-2 pt-8">
                                                    <CardElement
                                                        classes={{ base, focus, invalid }}
                                                        style={{ base: baseStripeStyle }}
                                                        hidePostalCode
                                                    />
                                                </div>
                                            </>
                                        );
                                    }}
                                </ClassNames>
                            </div>
                            <div className="mt-8">
                                <Button
                                    type="submit"
                                    color="green"
                                    size="md"
                                    data-testid="card-submit"
                                    className={cx('block w-full mt-4 text-16 font-medium', {
                                        'pointer-events-none opacity-60': isSubmitting,
                                    })}
                                >
                                    {buttonLabel}
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        );
    }
}

export default injectStripe(CardFields);
